import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Collapse,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  PlusOutlined,
  ScanOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import EventModal from './EventModal.jsx';
import './Events.scss';
import { useDispatch, useSelector } from 'react-redux';
import { deleteEvent, fetchEventById, fetchEvents } from '../../slices/eventSlice';
import { useNotification } from '../../Helper/NotificationProvider';
import { useNavigate } from 'react-router-dom';
import { fetchLocations } from '../../slices/locationSlice';
import { getData, patchData, postData } from '../../service/APIClient';
import { URL_EVENTS, URL_LOCATIONS, URL_TEMPLATE_IMAGE, URL_TEMPLATES } from '../../service/endpoints';
import dayjs from 'dayjs';
import { now } from 'moment';
import InProgressIcon from '../../Icons/InProgressIcon';
import TableBuilder from '../TableBuilder/TableBuilder';
import { deleteSchedule, fetchSchedules, patchSchedules } from '../../slices/scheduleSlice';
import AddScheduleModal from './AddScheduleModal';
import { addMatching } from '../../slices/matchingSlice';
import { fetchTemplates } from '../../slices/templatesSlice.js';
import TemplatePreview from '../TemplatePreview/TemplatePreview.jsx';

const { Panel } = Collapse;
const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Events = () => {
  const dispatch = useDispatch();
  const [filterType, setFilterType] = useState('All');
  const [filterLocation, setFilterLocation] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const navigate = useNavigate();
  const [currentEventId, setCurrentEventId] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [sortType, setSortType] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  const locations = useSelector(state => state.locations.data);
  const events = useSelector(state => state.events.data);
  const locationsStatus = useSelector(state => state.locations.status);
  const eventStatus = useSelector(state => state.events.status);
  const schedules = useSelector(state => state.schedules.data);
  const schedulesStatus = useSelector(state => state.schedules.status);
  const templates = useSelector(state => state.templates.data);
  const templatesStatus = useSelector(state => state.templates.status);

  const searchableColumns = ['templateName'];
  const sortableColumns = ['startTime', 'endTime'];
  const columnColors = [{ columnName: 'description', color: 'primary-color' }];

  const columns = [
    { title: 'Beschreibung', dataIndex: 'description', width: '20%', editable: true },
    { title: 'Template', dataIndex: 'templateName', width: '15%', editable: true },
    {
      title: 'Start',
      dataIndex: 'startTime',
      width: '20%',
      inputType: "date",
      editable: true,
      render: (text) => dayjs(text).add(0, 'hour').format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'Ende',
      dataIndex: 'endTime',
      width: '20%',
      inputType: "date",
      editable: true,
      render: (text) => text ? dayjs(text).format('DD.MM.YYYY HH:mm') : 'Kein Enddatum' // ✅ Safe
    }
  ];

  useEffect(() => {
    if (locationsStatus === 'idle') {
      dispatch(fetchLocations());
    }
  }, [locationsStatus, dispatch]);

  useEffect(() => {
    if (eventStatus === 'idle') {
      dispatch(fetchEvents());
    }
  }, [eventStatus, dispatch]);

  useEffect(() => {
    if (schedulesStatus === 'idle') {
      dispatch(fetchSchedules());
    }
  }, [schedulesStatus, dispatch]);

  useEffect(() => {
    if (templatesStatus === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [templatesStatus, dispatch]);

  const handleAddEvent = () => {
    setEditingEvent(null);
    setIsModalVisible(true);
  };

  const handleAddSchedule = (eventId) => {
    setCurrentEventId(eventId);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(prepareEventForModal(event));
    setIsModalVisible(true);
  };

  const handleAddMatchings = async (event) => {
    navigate('/events/matchings', {
      state: {
        id: event.key
      }
    });
  };

  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Event löschen',
      icon: <ExclamationCircleFilled />,
      content: 'Sind Sie sicher, dass Sie dieses Event löschen möchten?',
      okText: 'Ja',
      okType: 'danger',
      cancelText: 'Nein',
      onOk() {
        handleDelete(id);
      },
      onCancel() {
      }
    });
  };

  const handleDelete = id => {
    dispatch(deleteEvent(id))
      .then(() => {
        openNotification(
          'bottomRight',
          'Gelöscht',
          'Event erfolgreich gelöscht.',
          true
        );
        dispatch(fetchEvents());
      })
      .catch(() => {
        openNotification(
          'bottomRight',
          'Fehler',
          'Fehler beim Löschen des Events.',
          false
        );
      });
  };

  const handleFormSubmit = async (values) => {

    const {
      name,
      location,
      timeRange: [startTime, endTime],
      description,
      template,
    } = values;



    try {
      if (editingEvent?.id != null) {

        let updatedGuestList = editingEvent.guestList;

        const templateChanged = editingEvent?.template?.label !== template.label;
        const locationChanged = editingEvent?.location !== location;

        if (locationChanged) {
          // Fetch the full location from the API to get the complete layout (Redux cache may be truncated)
          const newLocation = await getData(`${URL_LOCATIONS}/${location}`);
          if (newLocation?.layout) {
            updatedGuestList = newLocation.layout.map(item => ({
              chair: item.chairId,
              matching: {
                matched: false,
                label: null,
                template: template.label,
                customFields: {}
              }
            }));
          }
        } else if (templateChanged && Array.isArray(updatedGuestList)) {
          updatedGuestList = updatedGuestList.map((guest) => {
            return {
              ...guest,
              matching: {
                ...guest.matching,
                template: template.label
              }
            };
          });
          console.log(updatedGuestList)
        }

        // Build the labels payload (for addMatching) from the updated list
        const labels = (updatedGuestList || [])
          .filter(g => g.matching?.label && g.matching.matched)
          .map(g => ({
            objectId: g.matching.label,
            data: [
              {
                zoneId: 'template',
                value: template.label
              }
            ]
          }));

        console.log(labels)

        const eventData = {
          name,
          location,
          startTime,
          endTime,
          description,
          templateId: template.value,
          templateName: template.label,
          guestList: updatedGuestList
        };

        console.log(eventData)

        await patchData(`${URL_EVENTS}/${editingEvent.id}`, eventData);

        if (labels.length > 0) {
          await dispatch(addMatching({ labels }));
          openNotification(
            'bottomRight',
            'Template geändert',
            `Es wurden ${labels.length} Labels auf das Template "${template.label}" geändert.`,
            true
          );
        } else {
          openNotification(
            'bottomRight',
            'Event geändert',
            'Event wurde erfolgreich angepasst.',
            true
          );
        }
      } else {

        const eventData = {
          name,
          location,
          startTime,
          endTime,
          description,
          templateId: template.value,
          templateName: template.label,
          guestList: []
        };

        console.log(eventData)

        // Create new event
        await postData(URL_EVENTS, eventData);
        openNotification(
          'bottomRight',
          'Event hinzugefügt',
          'Neues Event wurde erfolgreich angelegt.',
          true
        );


        //
        // if (labels.length > 0) {
        //   await dispatch(addMatching({ labels }));
        // }
      }

      // Refresh and close
      dispatch(fetchEvents());
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error adding/editing event:', error);
      openNotification(
        'bottomRight',
        'Fehler',
        'Event konnte nicht hinzugefügt/angepasst werden',
        false
      );
    }
  };

  const filterEvents = () => {
    const now = dayjs();

    return events.filter(event => {
      const startTime = dayjs(event.startTime);
      const endTime = dayjs(event.endTime);

      const matchesType =
        filterType === 'All' ||
        (filterType === 'Upcoming' && startTime.isAfter(now)) ||
        (filterType === 'Active' &&
          startTime.isBefore(now) &&
          endTime.isAfter(now)) ||
        (filterType === 'Done' && endTime.isBefore(now));

      const matchesLocation =
        !filterLocation || event?.locationName === filterLocation?.name;

      let matchesDateRange = true;
      if (dateRange[0] && dateRange[1]) {
        const filterStart = dateRange[0];
        const filterEnd = dateRange[1];
        // Overlap check: (eventStart < filterEnd) AND (eventEnd > filterStart)
        matchesDateRange =
          startTime.isBefore(filterEnd) && endTime.isAfter(filterStart);
      }

      return matchesType && matchesLocation && matchesDateRange;
    });
  };


  const prepareEventForModal = (event) => {
    return {
      id: event?.key,
      name: event?.name,
      location: event?.location,
      startTime: dayjs(event?.startTime),
      endTime: dayjs(event?.endTime),
      description: event?.description,
      guestList: event.guestList ?? [],
      template: {
        value: event?.templateId,
        label: event?.templateName
      },
    }
  }

  const handleEditSchedule = async (key, values) => {
    try {
      await dispatch(patchSchedules({ scheduleId: key, updateData: { ...values } }));
      await dispatch(fetchSchedules());
      openNotification(
        'bottomRight',
        'Geändert',
        'Bildwechsel erfolgreich geändert.',
        true
      );
    } catch (error) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Ändern des Bildwechsel fehlgeschlagen.',
        false
      );
    }
  };

  const handleDeleteSchedule = key => {
    try {
      dispatch(deleteSchedule(key)).then(() => {
        dispatch(fetchSchedules());
        openNotification(
          'bottomRight',
          'Gelöscht',
          'Bildwechsel erfolgreich gelöscht.',
          true
        );
      });
    } catch (error) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Löschen des Bildwechsel fehlgeschlagen.',
        false
      );
    }
  };

  function getEventStatus(event) {
    const nowTime = dayjs();
    const start = dayjs(event.startTime);
    const end = dayjs(event.endTime);

    if (nowTime.isBefore(start)) {
      return 'upcoming'; // or 'planned'
    } else if (nowTime.isBefore(end) && nowTime.isAfter(start)) {
      return 'in-progress';
    } else {
      return 'completed';
    }
  }

  const filteredEvents = filterEvents();
  let sortedEvents = [...filteredEvents];

  if (sortType !== '') {
    sortedEvents.sort((a, b) => {
      if (sortType === 'status') {
        const statusOrder = {
          upcoming: 1,
          'in-progress': 2,
          completed: 3
        };
        // get numeric for each event’s status
        const aVal = statusOrder[getEventStatus(a)] || 9999;
        const bVal = statusOrder[getEventStatus(b)] || 9999;
        return aVal - bVal;
      } else if (sortType === 'startTime') {
        return dayjs(a.startTime).diff(dayjs(b.startTime));
      }
      return 0;
    });
    if (sortOrder === 'desc') {
      sortedEvents.reverse();
    }
  }

  const getCardTitle = (event) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {dayjs(now()).isBefore(dayjs(event.startTime)) ?
          <ClockCircleOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#F69300' }} />
          : (dayjs(now()).isBefore(dayjs(event.endTime)) && dayjs(now()).isAfter(dayjs(event.startTime)) ?
              <InProgressIcon style={{ fontSize: '24px', marginRight: '8px' }} />
              : <CheckCircleOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#989898' }} />
          )
        }

        <span>
      {event?.name}
    </span>
      </div>
    );
  };

  const copyEventConfirm = (event) => {
    let newEventName = ''; // Store the input value

    confirm({
      title: 'Event kopieren',
      icon: <ExclamationCircleFilled />,
      content: (
        <Form layout="vertical">
          <Form.Item
            label="Neuer Eventname"
            name="eventName"
            rules={[{ required: true, message: 'Bitte geben Sie einen neuen Eventnamen ein!' }]}
          >
            <Input
              placeholder="Geben Sie den neuen Eventnamen ein"
              onChange={(e) => (newEventName = e.target.value)}
            />
          </Form.Item>
        </Form>
      ),
      okText: 'Kopieren',
      okType: 'primary',
      cancelText: 'Abbrechen',
      async onOk() {
        if (!newEventName.trim()) {
          Modal.error({
            title: 'Fehler',
            content: 'Bitte geben Sie einen gültigen Eventnamen ein!'
          });
          throw new Error('Input validation failed');
        }
        const { createdAt, updatedAt, _id, ...rest } = event;
        const newEvent = {
          ...rest,
          name: newEventName // Add the new name
        };
        await postData(URL_EVENTS, newEvent);
        openNotification('bottomRight', 'Event kopiert', 'Event wurde erfolgreich kopiert.', true);
        dispatch(fetchEvents());
      },
      onCancel() {
      }
    });
  };

  return (
    <ConfigProvider>
      <div style={{ margin: '10px' }}>
        <Button
          className="add-event-button"
          onClick={handleAddEvent}
          icon={<PlusOutlined />}
          type="primary"
          style={{ backgroundColor: 'black', marginBottom: 1 }}
        >
          {'Event hinzufügen'}
        </Button>
        <Card style={{ height: 60, marginTop: 10, marginBottom: 10 }}>
          <div style={{
            display: 'flex',
            flexFlow: 'row',
            alignItems: 'center',
            marginLeft: -31,
            position: 'relative',
            top: '-10px'
          }}>
            <EventModal
              isModalVisible={isModalVisible}
              setIsModalVisible={(visible) => {
                if (!visible) {
                  setEditingEvent(null); // Clear editing state on close
                }
                setIsModalVisible(visible);
              }}
              handleFormSubmit={handleFormSubmit}
              initialValues={editingEvent}
              locations={locations}
            />

            <Select
              value={filterType}
              onChange={(value) => setFilterType(value)}
              style={{ width: 130, marginLeft: 20 }}
              placeholder={t('Event-Typ auswählen')}
            >
              <Option value="All">{t('Alle')}</Option>
              <Option value="Upcoming">{t('Bevorstehend')}</Option>
              <Option value="Active">{t('Aktiv')}</Option>
              <Option value="Done">{t('Beendet')}</Option>
            </Select>

            <Select
              value={filterLocation ? filterLocation.name : 'Alle locations'}
              onChange={(value) => {
                if (value === 'Alle Locations')
                  setFilterLocation('');
                setFilterLocation(locations.find(location => location._id === value));
              }}
              style={{ width: 200, marginLeft: 20 }}
              placeholder={t('Location auswählen')}
            >
              <Option value="">{t('Alle Locations')}</Option>
              {locations.map(location => (
                <Option key={location._id} value={location._id}>
                  {location.name}
                </Option>
              ))}
            </Select>

            <RangePicker
              style={{ marginLeft: 20 }}
              value={dateRange}
              onChange={(dates) => {
                if (!dates) {
                  setDateRange([null, null]);
                } else {
                  setDateRange(dates);
                }
              }}
              placeholder={[t('Startdatum'), t('Enddatum')]}
            />

            <Select
              style={{ width: 140, marginLeft: 20 }}
              placeholder="Sortieren nach"
              value={sortType}
              onChange={(value) => setSortType(value)}
              allowClear
            >
              <Option value="status">Status</Option>
              <Option value="startTime">Startzeit</Option>
            </Select>

            <Tooltip title="Aufsteigend / Absteigend">
              <Button
                style={{ marginLeft: 8 }}
                color="default"
                variant="text"
                disabled={!sortType}
                onClick={() => {
                  setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
                }}
                icon={
                  sortOrder === 'asc' ? (
                    <ArrowUpOutlined />
                  ) : (
                    <ArrowDownOutlined />
                  )
                }
              >
              </Button>
            </Tooltip>


          </div>
        </Card>
        {/* Event Cards */}
        <Row gutter={[16, 16]}>
          {sortedEvents?.map(event => {
            return (
              <Col span={24} key={event?.key}>
                <Card
                  hoverable
                  title={getCardTitle(event)}
                  bordered={false}
                  style={{ width: '100%' }}
                  extra={
                    <>
                      <Tooltip title={'ESL verknüpfen'}>
                        <Button
                          type="text"
                          shape="default"
                          onClick={() => handleAddMatchings(event)}
                          icon={<ScanOutlined />}
                        />
                      </Tooltip>
                      <Tooltip title={'Bildwechsel'}>
                        <Button
                          type="text"
                          shape="default"
                          onClick={() => handleAddSchedule(event.key)}
                          icon={<CalendarOutlined />}
                        />
                      </Tooltip>
                      <Tooltip title={'Kopieren'}>
                        <Button
                          type="text"
                          shape="default"
                          onClick={() => copyEventConfirm(event)}
                          icon={<CopyOutlined />}
                        />
                      </Tooltip>
                      <Tooltip title={'Bearbeiten'}>
                        <Button
                          type="text"
                          shape="default"
                          onClick={() => handleEditEvent(event)}
                          icon={<EditOutlined />}
                        />
                      </Tooltip>
                      <Tooltip title={'Löschen'}>
                        <Button
                          type="text"
                          shape="default"
                          onClick={() => showDeleteConfirm(event?.key)}
                          icon={<DeleteOutlined />}
                        />
                      </Tooltip>
                    </>
                  }
                >
                  <Row align="middle" gutter={16}>
                    <Col>
                      <p>
                        <strong>{t('Location')}:</strong> {event?.locationName}
                      </p>
                      <p>
                        <strong>{t('Datum')}:</strong>{' '}
                        {dayjs(event?.startTime).format('DD.MM.YYYY HH:mm') + ' - ' + dayjs(event?.endTime).format('DD.MM.YYYY HH:mm')}
                      </p>
                      <p>
                        <strong>{t('Template')}:</strong> {event?.templateName}
                      </p>
                    </Col>
                    <Col> 
                      {(() => {
                        const template = templates?.find(template => template?.name === event?.templateName);

                        return <TemplatePreview 
                          templateName={template?.name}
                          sourceCode={template?.sourceCode} 
                        />
                      })()}
                    </Col>
                  </Row>
                  {(() => {
                    const matchedShapesCount =
                      event?.guestList?.filter(guest => guest.matching.matched).length || 0;
                    return (
                      <>
                        <Text style={{ color: 'black', fontWeight: 'bold' }} type="secondary">
                          Verknüpft: {matchedShapesCount} / {event?.guestList?.length || 0}
                        </Text>
                        {matchedShapesCount !== 0 && matchedShapesCount === event?.guestList?.length &&
                          <Tooltip title="Alle Stühle verknüpft.">
                            <CheckCircleOutlined
                              style={{ fontSize: '14px', color: '#4CAF50', margin: '0 8px' }}
                            />
                          </Tooltip>
                        }
                        {matchedShapesCount > 0 && matchedShapesCount < event?.guestList?.length &&
                          <Tooltip title="Nicht alle Stühle verknüpft.">
                            <ExclamationCircleOutlined
                              style={{ fontSize: '14px', color: '#FF9800', margin: '0 8px' }}
                            />
                          </Tooltip>
                        }
                        {matchedShapesCount === 0 &&
                          <Tooltip title="Keine Stühle verknüpft!">
                            <CloseCircleOutlined
                              style={{ fontSize: '14px', color: '#F44336', margin: '0 8px' }}
                            />
                          </Tooltip>
                        }
                      </>
                    );
                  })()}
                  <Collapse bordered={false} style={{ marginTop: '10px' }}>
                    <Panel header={t('Zeitpläne anzeigen')} key={`schedule-${event?.key}`}>
                      {event?.description &&
                        (<p>
                          <strong>{t('Beschreibung')}:</strong> {event?.description}
                        </p>)
                      }
                      <p>
                        <strong>{t('Eventdauer')}:</strong>{' '}
                        {dayjs(event?.endTime).diff(dayjs(event?.startTime), 'days')} {t('Tage')}
                      </p>
                      <TableBuilder
                        title={'Zeitplan'}
                        data={schedules.filter((schedule) => schedule.eventId === event.key)}
                        columns={columns}
                        searchableColumns={searchableColumns}
                        sortableColumns={sortableColumns}
                        columnColors={columnColors}
                        typfilter={{}}
                        expandable={false}
                        loading={schedulesStatus === 'loading'}
                        handleSave={handleEditSchedule}
                        handleDelete={handleDeleteSchedule}
                      />
                    </Panel>
                    <AddScheduleModal
                      event={event}
                      currentEventId={currentEventId}
                      setCurrentEventId={setCurrentEventId}
                    />
                  </Collapse>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </ConfigProvider>
  );
};

export default Events;
